// =============================================================================
// File: frontend/src/components/TwinSchedule.jsx
// Digital Twin Schedule Tab — PK-optimized medication/supplement timing
// Patent claims exercised: P-013 C1-C9
// =============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../config';
import CostChip from './CostChip';
import XaiPromptPanel from './XaiPromptPanel';
import { CitationChip } from './Citations';
import { useCreditSpend } from '../contexts/CreditSpendContext';

/** Simple markdown → HTML for AI explanations (bold, line breaks, lists) */
function renderMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')  // escape HTML
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')                      // **bold**
    .replace(/\*(.+?)\*/g, '<em>$1</em>')                                  // *italic*
    .replace(/^[-•]\s+(.+)$/gm, '<li>$1</li>')                             // bullet lists
    .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')                         // wrap in <ul>
    .replace(/\n\n/g, '</p><p>')                                            // paragraphs
    .replace(/\n/g, '<br>')                                                 // line breaks
    .replace(/^/, '<p>').replace(/$/, '</p>');                               // wrap in <p>
}

// Timeline block colors (via CSS classes)
const SLOT_TYPE_CLASS = {
  meal: 'dt-slot-meal',
  medication: 'dt-slot-medication',
  supplement: 'dt-slot-supplement',
};

export default function TwinSchedule({ fetchWithAuth }) {
  const { spend } = useCreditSpend();  // #77 twin_schedule = 1 credit (subtle chip, low-balance confirm)
  const [activeSchedule, setActiveSchedule] = useState(null);
  const [proposals, setProposals] = useState(null);
  const [pkProfiles, setPkProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);
  const [activating, setActivating] = useState(false);
  const [scheduleView, setScheduleView] = useState('weekday');

  const loadSchedule = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      const res = await fetchWithAuth(`${API_BASE}/api/twin/schedule`);
      if (res.ok) {
        const data = await res.json();
        setActiveSchedule(data);
      }
    } catch (err) {
      console.error('Failed to load schedule:', err);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  const loadPKProfiles = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/twin/pk-profiles`);
      if (res.ok) {
        const data = await res.json();
        setPkProfiles(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Failed to load PK profiles:', err);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadSchedule(true);  // Initial load shows spinner
    loadPKProfiles();
  }, [loadSchedule, loadPKProfiles]);

  const handleOptimize = async () => {
    setOptimizing(true);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/twin/schedule/optimize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ n_proposals: 2 }),
      });
      if (res.ok) {
        const data = await res.json();
        if (!data.proposals || data.proposals.length === 0) {
          alert('No schedule proposals generated. Add medications or supplements to your profile first, then try again.');
        } else {
          setProposals(data);
        }
      } else {
        const err = await res.json();
        alert(Array.isArray(err.detail) ? err.detail.map(d => d.msg || d).join(', ') : err.detail || 'Optimization failed');
      }
    } catch (err) {
      alert('Network error during schedule optimization');
    } finally {
      setOptimizing(false);
    }
  };

  const handleActivate = async (scheduleId) => {
    try {
      setActivating(true);
      const res = await fetchWithAuth(`${API_BASE}/api/twin/schedule/${scheduleId}/activate`, { method: 'POST' });
      if (res.ok) {
        loadSchedule();
        setProposals(null);
      }
    } catch (err) {
      console.error('Failed to activate schedule:', err);
    } finally {
      setActivating(false);
    }
  };

  // Convert time string to pixel position on 24-hour timeline
  const timeToPosition = (timeStr) => {
    if (!timeStr) return 0;
    const parts = timeStr.split(':');
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    return ((hours * 60 + minutes) / (24 * 60)) * 100;
  };

  const renderTimeline = (timeSlots) => {
    if (!timeSlots || timeSlots.length === 0) return null;

    return (
      <div className="dt-timeline">
        {/* Hour markers */}
        <div className="dt-timeline-hours">
          {[0, 3, 6, 9, 12, 15, 18, 21].map(h => (
            <span
              key={h}
              className="dt-timeline-hour"
              style={{ left: `${(h / 24) * 100}%` }}
            >
              {h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`}
            </span>
          ))}
        </div>

        {/* Sleep zone (gray background) */}
        <div className="dt-timeline-sleep" style={{ left: '0%', width: `${(7 / 24) * 100}%` }}></div>
        <div className="dt-timeline-sleep" style={{ left: `${(22 / 24) * 100}%`, width: `${(2 / 24) * 100}%` }}></div>

        {/* Time slot blocks */}
        <div className="dt-timeline-track">
          {timeSlots.map((slot, i) => {
            const pos = timeToPosition(slot.time);
            const slotClass = SLOT_TYPE_CLASS[slot.type] || 'dt-slot-medication';

            return (
              <div
                key={i}
                className={`dt-timeline-block ${slotClass}`}
                style={{ left: `${pos}%` }}
                title={`${slot.time}: ${slot.items.map(it => it.name).join(', ')}`}
              >
                <span className="dt-timeline-block-time">{slot.time}</span>
                <div className="dt-timeline-block-items">
                  {slot.items.map((item, j) => (
                    <span key={j} className="dt-timeline-block-item">
                      {item.name}
                      {item.dosage && <em> ({item.dosage})</em>}
                    </span>
                  ))}
                </div>
                {slot.notes && (
                  <span className="dt-timeline-block-notes">{slot.notes}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="dt-timeline-legend">
          <span className="dt-legend-item"><span className="dt-legend-swatch dt-slot-meal"></span> Meals</span>
          <span className="dt-legend-item"><span className="dt-legend-swatch dt-slot-medication"></span> Medications</span>
          <span className="dt-legend-item"><span className="dt-legend-swatch dt-slot-supplement"></span> Supplements</span>
          <span className="dt-legend-item"><span className="dt-legend-swatch dt-timeline-sleep"></span> Sleep</span>
        </div>
      </div>
    );
  };

  return (
    <div className="dt-schedule">
      {/* AI Operation Spinner Overlay */}
      {optimizing && (
        <div className="dt-aiOverlay">
          <div className="dt-aiOverlay-content">
            <div className="dt-spinner"></div>
            <p>Optimizing schedule — analyzing drug interactions and timing...</p>
            <p className="dt-aiOverlay-sub">This may take 15-30 seconds</p>
          </div>
        </div>
      )}
      {activating && (
        <div className="dt-aiOverlay">
          <div className="dt-aiOverlay-content">
            <div className="dt-spinner"></div>
            <p>Activating schedule...</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="dt-scheduleHeader">
        <h3>Medication & Supplement Schedule</h3>
        <div className="dt-scheduleActions">
          <div className="dt-toggleGroup">
            <button
              className={`dt-toggleBtn ${scheduleView === 'weekday' ? 'active' : ''}`}
              onClick={() => setScheduleView('weekday')}
            >
              Weekday
            </button>
            <button
              className={`dt-toggleBtn ${scheduleView === 'weekend' ? 'active' : ''}`}
              onClick={() => setScheduleView('weekend')}
            >
              Weekend
            </button>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => spend('twin_schedule', 'Optimize this schedule?', handleOptimize)}
            disabled={optimizing}
          >
            {optimizing ? 'Optimizing...' : <>Optimize Schedule <CostChip apiType="twin_schedule" /></>}
          </button>
        </div>
      </div>

      {/* Active Schedule */}
      {loading ? (
        <div className="dt-loading"><div className="dt-spinner"></div></div>
      ) : activeSchedule?.schedules?.length > 0 ? (
        <div className="dt-activeSchedule">
          <h4>Current Active Schedule</h4>
          {activeSchedule.schedules
            .filter(s => s.schedule_type === scheduleView)
            .map(schedule => (
              <div key={schedule.id} className="dt-scheduleCard">
                {renderTimeline(schedule.time_slots)}
                <div className="dt-scheduleCard-meta">
                  <span>Interference: {schedule.interference_score.toFixed(2)}</span>
                  <span>Difficulty: {schedule.adherence_difficulty}</span>
                </div>
                {schedule.ai_explanation && (
                  <div className="dt-aiExplanation">
                    <div className="dt-aiExplanation" dangerouslySetInnerHTML={{__html: renderMarkdown(schedule.ai_explanation)}} />
                  </div>
                )}
                {/* Apple 1.4.1 — medication TIMING guidance is clinical advice and must show its
                    reasoning and citations, exactly like every other AI surface. featureName
                    drives the baseline citations in xaiHelpers, so the Citations tab is populated
                    even when no condition-specific template matches. */}
                {schedule.prompt_data && (
                  <XaiPromptPanel
                    promptData={schedule.prompt_data}
                    featureName="Medication Schedule Optimization"
                  />
                )}
              </div>
            ))}
        </div>
      ) : !proposals && (
        <div className="dt-empty">
          <h3>No Active Schedule</h3>
          <p>Click "Optimize Schedule" to generate personalized medication and supplement timing proposals
          based on your pharmacokinetic profiles and daily routine.</p>
        </div>
      )}

      {/* Schedule Proposals */}
      {proposals && proposals.proposals?.length > 0 && (
        <div className="dt-proposals">
          <h4>Schedule Proposals</h4>
          <p className="dt-proposals-current">
            Current interference score: {proposals.current_interference?.toFixed(2) || '—'}
          </p>
          <div className="dt-proposalGrid">
            {proposals.proposals.map(proposal => (
              <div key={proposal.id} className="dt-proposalCard">
                <div className="dt-proposalCard-header">
                  <span className="dt-proposalCard-rank">
                    Proposal #{proposal.proposal_rank} ({proposal.schedule_type})
                  </span>
                  <span className="dt-proposalCard-score">
                    Interference: {proposal.interference_score.toFixed(2)}
                  </span>
                </div>

                {renderTimeline(proposal.time_slots)}

                <div className="dt-proposalCard-meta">
                  <span>Difficulty: {proposal.adherence_difficulty}</span>
                  {proposal.resolved_interactions?.length > 0 && (
                    <span className="dt-resolved">
                      {proposal.resolved_interactions.length} conflicts resolved
                    </span>
                  )}
                  {proposal.residual_conflicts?.length > 0 && (
                    <span className="dt-residual">
                      {proposal.residual_conflicts.length} soft conflicts remain
                    </span>
                  )}
                </div>

                {proposal.ai_explanation && (
                  <div className="dt-aiExplanation">
                    <div className="dt-aiExplanation" dangerouslySetInnerHTML={{__html: renderMarkdown(proposal.ai_explanation)}} />
                  </div>
                )}

                {proposal.prompt_data && (
                  <XaiPromptPanel
                    promptData={proposal.prompt_data}
                    featureName="Medication Schedule Optimization"
                  />
                )}

                <button
                  className="btn btn-primary dt-activateBtn"
                  onClick={() => handleActivate(proposal.id)}
                >
                  Activate This Schedule
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PK Profiles */}
      {pkProfiles.length > 0 && (
        <div className="dt-pkProfiles">
          <h4>Pharmacokinetic Profiles</h4>
          <div className="dt-pkGrid">
            {pkProfiles.map((profile, i) => {
              // F-17 (Apple 1.4.1): every PK number says where it comes from. A verified profile shows its
              // source and, once the sourcing pass has linked it, its citation. An AI estimate / unconfirmed
              // source is shown as exactly that and is never used for the schedule (the server excludes it).
              const pk = profile.pk_profile || profile.pk_estimate;
              const verified = !!profile.pk_profile;
              // F-17 (Vincent 2026-10-21): a verified profile shows the LABEL's own wording for each value, and
              // "Not stated in the label" where it says nothing — never a number the label does not give.
              const ev = (pk && pk.label_evidence && pk.label_evidence.fields) || {};
              const shown = (key, num) => (verified
                ? ((ev[key] && ev[key].text) || 'Not stated in the label')
                : (num ? `${num} min` : '—'));
              return (
              <div key={i} className="dt-pkCard" data-testid="pk-card">
                <h5>{profile.medication}</h5>
                {pk ? (
                  <>
                    {!verified && (
                      <p className="dt-pkUnverified" data-testid="pk-not-verified">
                        <span className="dt-pkUnverified-badge">{pk.source_label || 'Estimate — not verified'}</span>
                        Not used for your schedule. Ask your pharmacist or clinician about timing.
                      </p>
                    )}
                    <div className={`dt-pkCard-details${verified ? '' : ' dt-pkCard-details--unverified'}`}>
                      <span data-testid="pk-onset">Onset: {shown('onset', pk.absorption_onset_min)}</span>
                      <span data-testid="pk-peak">Peak: {shown('tmax', pk.tmax_min)}</span>
                      <span data-testid="pk-half-life">Half-life: {shown('half_life', pk.half_life_min)}</span>
                      <span>TI: {pk.therapeutic_index || '—'}</span>
                      <span data-testid="pk-food">Food: {verified
                        ? shown('food')
                        : (pk.food_effect?.replace(/_/g, ' ') || '—')}</span>
                    </div>
                    {verified && (
                      <div className="dt-pkSource" data-testid="pk-source">
                        <span>Source: {pk.source_label}{pk.label_form ? ` — ${pk.label_form}` : ''}</span>
                        {pk.citation ? (
                          <CitationChip
                            sources={[pk.citation]}
                            featureName="Medication Schedule Optimization"
                            itemTitle={`${profile.medication} — timing data`}
                            testId="pk-citation"
                          />
                        ) : (
                          <span className="dt-pkSource-pending" data-testid="pk-citation-pending">
                            Citation not linked yet
                          </span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="dt-empty-text" data-testid="pk-none">No verified timing data on file</p>
                )}
                {profile.personalized && (
                  <div className="dt-pkCard-personalized">
                    <span className="dt-personalized-badge">Personalized</span>
                    <span>Confidence: {(profile.personalized.confidence * 100).toFixed(0)}%</span>
                    <span>Samples: {profile.personalized.sample_count}</span>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
